import type { FC } from 'react'

export interface SidebarIconProps {
  className?: string
  color?: string
  size?: number | string
  title?: string
}

const getIconProps = ({ className, color, size, title }: SidebarIconProps) => ({
  className,
  width: size ?? '1em',
  height: size ?? '1em',
  style: color ? { color } : undefined,
  'aria-hidden': title ? undefined : true,
  'aria-label': title,
  role: title ? 'img' : 'presentation',
})

/**
 * Sidebar icon for the General Settings tab.
 */
export const GeneralSettingsIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 20 20'
      fill='none'
      {...iconProps}
    >
      <path
        d='M17.1593 10.98C17.1993 10.66 17.2293 10.34 17.2293 10C17.2293 9.66 17.1993 9.34 17.1593 9.02L19.2693 7.37C19.4593 7.22 19.5093 6.95 19.3893 6.73L17.3893 3.27C17.2993 3.11 17.1293 3.02 16.9493 3.02C16.8893 3.02 16.8293 3.03 16.7793 3.05L14.2893 4.05C13.7693 3.65 13.2093 3.32 12.5993 3.07L12.2193 0.42C12.1893 0.18 11.9793 0 11.7293 0L7.72933 0C7.47933 0 7.26933 0.18 7.23933 0.42L6.85933 3.07C6.24933 3.32 5.68933 3.66 5.16933 4.05L2.67933 3.05C2.61933 3.03 2.55933 3.02 2.49933 3.02C2.32933 3.02 2.15933 3.11 2.06933 3.27L0.0693316 6.73C-0.0606684 6.95 -0.000668302 7.22 0.189332 7.37L2.29933 9.02C2.25933 9.34 2.22933 9.67 2.22933 10C2.22933 10.33 2.25933 10.66 2.29933 10.98L0.189332 12.63C-0.000668302 12.78 -0.0506684 13.05 0.0693316 13.27L2.06933 16.73C2.15933 16.89 2.32933 16.98 2.50933 16.98C2.56933 16.98 2.62933 16.97 2.67933 16.95L5.16933 15.95C5.68933 16.35 6.24933 16.68 6.85933 16.93L7.23933 19.58C7.26933 19.82 7.47933 20 7.72933 20H11.7293C11.9793 20 12.1893 19.82 12.2193 19.58L12.5993 16.93C13.2093 16.68 13.7693 16.34 14.2893 15.95L16.7793 16.95C16.8393 16.97 16.8993 16.98 16.9593 16.98C17.1293 16.98 17.2993 16.89 17.3893 16.73L19.3893 13.27C19.5093 13.05 19.4593 12.78 19.2693 12.63L17.1593 10.98ZM15.1793 9.27C15.2193 9.58 15.2293 9.79 15.2293 10C15.2293 10.21 15.2093 10.43 15.1793 10.73L15.0393 11.86L15.9293 12.56L17.0093 13.4L16.3093 14.61L15.0393 14.1L13.9993 13.68L13.0993 14.36C12.6693 14.68 12.2593 14.92 11.8493 15.09L10.7893 15.52L10.6293 16.65L10.4293 18H9.02933L8.67933 15.52L7.61933 15.09C7.18933 14.91 6.78933 14.68 6.38933 14.38L5.47933 13.68L4.41933 14.11L3.14933 14.62L2.44933 13.41L3.52933 12.57L4.41933 11.87L4.27933 10.74C4.24933 10.43 4.22933 10.2 4.22933 10C4.22933 9.8 4.24933 9.57 4.27933 9.27L4.41933 8.14L3.52933 7.44L2.44933 6.6L3.14933 5.39L4.41933 5.9L5.45933 6.32L6.35933 5.64C6.78933 5.32 7.19933 5.08 7.60933 4.91L8.66933 4.48L8.82933 3.35L9.02933 2H10.4193L10.7693 4.48L11.8293 4.91C12.2593 5.09 12.6593 5.32 13.0593 5.62L13.9693 6.32L15.0293 5.89L16.2993 5.38L16.9993 6.59L15.9293 7.44L15.0393 8.14L15.1793 9.27ZM9.72933 6C7.51933 6 5.72933 7.79 5.72933 10C5.72933 12.21 7.51933 14 9.72933 14C11.9393 14 13.7293 12.21 13.7293 10C13.7293 7.79 11.9393 6 9.72933 6ZM9.72933 12C8.62933 12 7.72933 11.1 7.72933 10C7.72933 8.9 8.62933 8 9.72933 8C10.8293 8 11.7293 8.9 11.7293 10C11.7293 11.1 10.8293 12 9.72933 12Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Payments tab.
 */
export const PaymentsIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      {...iconProps}
    >
      <path
        d='M19 14V6C19 4.9 18.1 4 17 4H3C1.9 4 1 4.9 1 6V14C1 15.1 1.9 16 3 16H17C18.1 16 19 15.1 19 14ZM17 14H3V6H17V14ZM10 7C8.34 7 7 8.34 7 10C7 11.66 8.34 13 10 13C11.66 13 13 11.66 13 10C13 8.34 11.66 7 10 7ZM23 7V18C23 19.1 22.1 20 21 20H4V18H21V7H23Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Header Customization tab.
 */
export const CustomizationIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      {...iconProps}
    >
      <path
        d='M12 22C6.49 22 2 17.51 2 12C2 6.49 6.49 2 12 2C17.51 2 22 6.04 22 11C22 14.31 19.31 17 16 17H14.23C13.95 17 13.73 17.22 13.73 17.5C13.73 17.62 13.78 17.73 13.86 17.83C14.27 18.3 14.5 18.89 14.5 19.5C14.5 20.88 13.38 22 12 22ZM12 4C7.59 4 4 7.59 4 12C4 16.41 7.59 20 12 20C12.28 20 12.5 19.78 12.5 19.5C12.5 19.34 12.42 19.22 12.36 19.15C11.95 18.69 11.73 18.1 11.73 17.5C11.73 16.12 12.85 15 14.23 15H16C18.21 15 20 13.21 20 11C20 7.14 16.41 4 12 4Z'
        fill='currentColor'
      />
      <path
        d='M6.5 13C7.32843 13 8 12.3284 8 11.5C8 10.6716 7.32843 10 6.5 10C5.67157 10 5 10.6716 5 11.5C5 12.3284 5.67157 13 6.5 13Z'
        fill='currentColor'
      />
      <path
        d='M9.5 9C10.3284 9 11 8.32843 11 7.5C11 6.67157 10.3284 6 9.5 6C8.67157 6 8 6.67157 8 7.5C8 8.32843 8.67157 9 9.5 9Z'
        fill='currentColor'
      />
      <path
        d='M14.5 9C15.3284 9 16 8.32843 16 7.5C16 6.67157 15.3284 6 14.5 6C13.6716 6 13 6.67157 13 7.5C13 8.32843 13.6716 9 14.5 9Z'
        fill='currentColor'
      />
      <path
        d='M17.5 13C18.3284 13 19 12.3284 19 11.5C19 10.6716 18.3284 10 17.5 10C16.6716 10 16 10.6716 16 11.5C16 12.3284 16.6716 13 17.5 13Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Player Waivers tab.
 */
export const WaiverIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      {...iconProps}
    >
      <path
        d='M19.1025 10.39L20.1625 9.33C20.9425 8.55 20.9425 7.28 20.1625 6.5L18.7525 5.09C17.9725 4.31 16.7025 4.31 15.9225 5.09L14.8625 6.15L19.1025 10.39ZM14.8625 11.81L7.6625 19H6.2525V17.59L13.4425 10.4L14.8625 11.81ZM13.4425 7.56L4.2525 16.76V21H8.4925L17.6825 11.81L13.4425 7.56ZM19.2525 17.5C19.2525 19.69 16.7125 21 14.2525 21C13.7025 21 13.2525 20.55 13.2525 20C13.2525 19.45 13.7025 19 14.2525 19C15.7925 19 17.2525 18.27 17.2525 17.5C17.2525 17.03 16.7725 16.63 16.0225 16.3L17.5025 14.82C18.5725 15.45 19.2525 16.29 19.2525 17.5ZM4.8325 13.35C3.8625 12.79 3.2525 12.06 3.2525 11C3.2525 9.2 5.1425 8.37 6.8125 7.64C7.8425 7.18 9.2525 6.56 9.2525 6C9.2525 5.59 8.4725 5 7.2525 5C5.9925 5 5.4525 5.61 5.4225 5.64C5.0725 6.05 4.4425 6.1 4.0225 5.76C3.6125 5.42 3.5325 4.81 3.8725 4.38C3.9825 4.24 5.0125 3 7.2525 3C9.4925 3 11.2525 4.32 11.2525 6C11.2525 7.87 9.3225 8.72 7.6125 9.47C6.6725 9.88 5.2525 10.5 5.2525 11C5.2525 11.31 5.6825 11.6 6.3225 11.86L4.8325 13.35Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Team Custom Form tab.
 */
export const TeamCustomFormIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      {...iconProps}
    >
      <path
        d='M7 16H14V18H7V16ZM7 12H17V14H7V12ZM7 8H17V10H7V8ZM19 4H14.82C14.4 2.84 13.3 2 12 2C10.7 2 9.6 2.84 9.18 4H5C4.86 4 4.73 4.01 4.6 4.04C4.21 4.12 3.86 4.32 3.59 4.59C3.41 4.77 3.26 4.99 3.16 5.23C3.06 5.46 3 5.72 3 6V20C3 20.27 3.06 20.54 3.16 20.78C3.26 21.02 3.41 21.23 3.59 21.42C3.86 21.69 4.21 21.89 4.6 21.97C4.73 21.99 4.86 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM12 3.75C12.41 3.75 12.75 4.09 12.75 4.5C12.75 4.91 12.41 5.25 12 5.25C11.59 5.25 11.25 4.91 11.25 4.5C11.25 4.09 11.59 3.75 12 3.75ZM19 20H5V6H19V20Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Player Custom Form tab.
 */
export const PlayerCustomFormIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 -960 960 960'
      fill='none'
      {...iconProps}
    >
      <path
        d='M216-216v-528 151-7 384Zm72-228h239.83q2.17-19 7.17-37t15-35H288v72Zm0 156h208q15-13 31.5-23.5T562-330q-5-7-9.5-14.5T544-360H288v72Zm0-312h384v-72H288v72Zm-72 456q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h528q29.7 0 50.85 21.15Q816-773.7 816-744v194q-15-15-33-26t-39-17v-151H216v528h241q-1 5-1 9.67V-144H216Zm412-219.77q-28-27.78-28-68Q600-472 627.77-500q27.78-28 68-28Q736-528 764-500.23q28 27.78 28 68Q792-392 764.23-364q-27.78 28-68 28Q656-336 628-363.77ZM528-144v-53q0-11.08 4-20.54 4-9.46 12-16.46 32-27 71-40.5t81-13.5q42 0 81 13.5t71 40.5q8 7 12 16.46t4 20.54v53H528Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Email Customization tab.
 */
export const EmailCustomizationIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 -960 960 960'
      fill='none'
      {...iconProps}
    >
      <path
        d='M432-528 120-707v347h456v72H120q-29.7 0-50.85-21.5Q48-331 48-360v-432q0-29.7 21.15-50.85Q90.3-864 120-864h624q29 0 50.5 21.15T816-792v264h-72v-179L432-528Zm0-85 312-179H120l312 179ZM768-96q-60 0-102-42t-42-102v-144q0-40.32 27.77-68.16 27.78-27.84 68-27.84 40.23 0 67.73 28 27.5 28 28.5 68v144h-72v-144q0-9.6-7-16.8-7-7.2-17-7.2t-17 7.2q-7 7.2-7 16.8v144q0 29.7 21.21 50.85 21.21 21.15 51 21.15t50.88-21.15q21.1-21.15 21.1-50.85v-144H912v144.42Q912-180 870-138T768-96ZM120-707v-85 432-347Z'
        fill='currentColor'
      />
    </svg>
  )
}

/**
 * Sidebar icon for the Integrations tab.
 */
export const IntegrationsIcon: FC<SidebarIconProps> = (props) => {
  const iconProps = getIconProps(props)

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 -960 960 960'
      fill='none'
      {...iconProps}
    >
      <path
        d='M792-96v-96h-96v-72h96v-96h72v96h96v72h-96v96h-72ZM144-144q-29.7 0-50.85-21.15Q72-186.3 72-216v-528q0-29.7 21.15-50.85Q114.3-816 144-816h528q29.7 0 50.85 21.15Q744-773.7 744-744v192h-72v-192H144v528h192v72H144Zm264-192v-72h192v72H408Zm0-144v-72h264v-72l120 108-120 108v-72H408Z'
        fill='currentColor'
      />
    </svg>
  )
}
